import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { useLocation } from '@/shared/hooks/useLocation';
import { useWorkflowStatus } from '@/shared/hooks/useWorkflows';
import { useAuthContext } from '@/shared/context/useAuthContext';
import { useAdsState } from '../hooks/useAdsState';
import { campaignsApi, type MarketingCampaign } from '../api/campaignsApi';
import { AdsConnectionFailed } from '../components/AdsConnectionFailed';
import { ModeSelectionScreen } from '../components/ModeSelectionScreen';
import { AutopilotWizard } from '../components/AutopilotWizard';
import { GuidedWizard } from '../components/GuidedWizard';
import { ActiveCampaignsView } from '../components/ActiveCampaignsView';
import { SyncNowButton } from '../components/SyncNowButton';
import { ManagementModeProvider } from '../components/AutopilotGuard';
import { CampaignCard } from '../components/CampaignCard';
import { NoActiveCampaignsEmptyState } from '../components/NoActiveCampaignsEmptyState';
import type { ExternalCampaignData } from '../components/externalCampaignData';
import type { ExistingCampaign } from '../components/ExistingCampaignRow';
import { PageHeader } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Lightbulb } from 'lucide-react';
import { useTriggerAdsSync } from '../hooks/useTriggerAdsSync';
import { campaignKeys } from '../queryKeys';
import styles from './CampaignsListPage.module.css';

/** Local phases within the active-campaigns branch — what the user is doing right now. */
type CampaignsPhase = 'browse' | 'pickMode' | 'wizard:autopilot' | 'wizard:guided';

/** Build ExternalCampaignData from a MarketingCampaign's child deployments. */
function toExternalData(mc: MarketingCampaign): ExternalCampaignData {
  const deployments = mc.campaigns ?? [];
  const spend = deployments.reduce((s, d) => s + (d.spend ?? 0), 0);
  const impressions = deployments.reduce((s, d) => s + (d.impressions ?? 0), 0);
  const clicks = deployments.reduce((s, d) => s + (d.clicks ?? 0), 0);
  const conversions = deployments.reduce((s, d) => s + (d.conversions ?? 0), 0);
  const dailyBudget = deployments.reduce((s, d) => s + (d.budget ?? 0), 0) || null;
  // Greyed as removed when every launched deployment is gone on the platform.
  const launched = deployments.filter((d) => !!d.externalId);
  const removed = launched.length > 0 && launched.every((d) => !!d.removedFromPlatformAt);
  return {
    id: mc.id,
    marketingCampaignId: mc.id,
    name: mc.name,
    objective: mc.objective,
    status: mc.status === 'live' ? 'active' : mc.status,
    removed,
    spend,
    impressions,
    clicks,
    conversions,
    cpa: conversions > 0 ? spend / conversions : null,
    dailyBudget,
    launches: deployments
      .filter((d): d is typeof d & { launchedAt: string } => !!d.launchedAt)
      .map((d) => ({ channel: d.channel ?? d.platform, launchedAt: d.launchedAt })),
  };
}

/** Project a MarketingCampaign down to the slim shape the wizards' existing-campaigns step needs. */
function toExistingCampaign(mc: MarketingCampaign): ExistingCampaign {
  const deployments = mc.campaigns ?? [];
  const spend = deployments.reduce((s, d) => s + (d.spend ?? 0), 0);
  const conversions = deployments.reduce((s, d) => s + (d.conversions ?? 0), 0);
  const dailyBudget = deployments.reduce((s, d) => s + (d.budget ?? 0), 0) || null;
  return {
    id: mc.id,
    name: mc.name,
    spend,
    conversions,
    dailyBudget,
    source: mc.source === 'external' ? 'external' : 'pn-ai',
  };
}

export function CampaignsListPage() {
  const navigate = useNavigate();
  const adsState = useAdsState();
  const queryClient = useQueryClient();
  const { currentLocation } = useLocation();
  const { user } = useAuthContext();
  const locationId = currentLocation?.id ?? '';
  const { syncNow, syncing } = useTriggerAdsSync();

  const { isRunning: campaignRecsRunning } = useWorkflowStatus(
    locationId,
    'campaign_recommendation',
    [[...campaignKeys.marketing()]],
  );

  const marketingCampaignsQuery = useQuery({
    queryKey: campaignKeys.marketingByLocation(locationId),
    queryFn: () => campaignsApi.list(locationId),
  });

  const locationConfigQuery = useQuery({
    queryKey: campaignKeys.locationAdsConfig(currentLocation?.id ?? ''),
    queryFn: () => campaignsApi.autopilot.getConfig(currentLocation!.id),
    enabled: !!currentLocation?.id,
  });

  useMutation({
    mutationFn: () => campaignsApi.recommend(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: campaignKeys.marketing() }),
  });

  const [phase, setPhase] = useState<CampaignsPhase>('browse');

  if (!adsState.loaded) {
    return (
      <div className={styles.page}>
        <PageHeader title="Campaigns" />
      </div>
    );
  }

  if (adsState.accounts.connectionFailed) {
    return (
      <AdsConnectionFailed
        title="Campaigns"
        className={styles.page}
        failures={adsState.accounts.failed}
      />
    );
  }

  if (!adsState.accounts.any) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Campaigns"
          subtitle="Connect an ad account to unlock the Campaigns tab."
        />
        <EmptyState
          title="Connect an Ad Platform"
          description="Meta Ads is managed by your agency. Connect Google Ads to import existing campaigns and unlock AI-generated recommendations."
          actions={
            <Button variant="primary" onClick={() => navigate('/settings/paid')}>
              Connect Google Ads
            </Button>
          }
        />
      </div>
    );
  }

  // Account connected, but nothing active/recommended/in-draft (dismissed-only or
  // truly empty). Distinct from the no-account state above.
  if (adsState.campaigns === 'none') {
    return (
      <div className={styles.page}>
        <PageHeader title="Campaigns" subtitle="Manage your ad campaigns" />
        <NoActiveCampaignsEmptyState strategy={adsState.strategy} />
      </div>
    );
  }

  const allMarketing: MarketingCampaign[] = marketingCampaignsQuery.data?.data ?? [];
  // Imported campaigns surface in their declared priority order; when a file
  // carries no priority, `priority` is the file position, so this stays file
  // order. Ties (and campaigns with no priority) fall back to created order.
  const bySortOrder = (a: MarketingCampaign, b: MarketingCampaign) => {
    const pa = a.priority ?? Number.POSITIVE_INFINITY;
    const pb = b.priority ?? Number.POSITIVE_INFINITY;
    if (pa !== pb) return pa - pb;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  };
  const pnCampaigns = allMarketing.filter((c) => c.source === 'pn-ai');
  const externalCampaigns = allMarketing.filter((c) => c.source === 'external').sort(bySortOrder);

  const recommendedCampaigns = pnCampaigns.filter((c) => c.status === 'recommended');
  const liveOrPending = pnCampaigns.filter((c) =>
    ['live', 'pending_creative', 'approved', 'launching', 'ready_for_review'].includes(c.status),
  );
  const externalData = externalCampaigns.map(toExternalData);

  // Wizard's "Your existing campaigns" step: both previously-launched PN campaigns
  // and live external imports. Without this, approving a new strategy would silently
  // pause prior PN campaigns (backend pauses anything not in keepExistingCampaignIds).
  const existingForWizard: ExistingCampaign[] = [
    ...liveOrPending.map(toExistingCampaign),
    ...externalCampaigns.filter((c) => c.status === 'live').map(toExistingCampaign),
  ].sort((a, b) => {
    if (a.source !== b.source) return a.source === 'pn-ai' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  // No active strategy and no PN campaigns yet — only imported externals to show,
  // nudge the user to create a strategy to unlock recommendations.
  if (adsState.strategy !== 'active' && pnCampaigns.length === 0) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Campaigns"
          subtitle="Imported from your ad accounts"
          actions={<SyncNowButton syncing={syncing} onSync={syncNow} />}
        />
        <div className={styles.purpleNudge}>
          <Lightbulb size={16} aria-hidden /> <strong>Create a marketing strategy</strong> to unlock
          AI-generated campaign recommendations.{' '}
          <button type="button" onClick={() => navigate('/strategy')}>
            Create strategy →
          </button>
        </div>
        {externalData.length === 0 ? (
          <div className={styles.empty}>No imported campaigns yet.</div>
        ) : (
          externalData.map((c) => (
            <CampaignCard
              key={c.id}
              variant="external"
              name={c.name}
              objective={c.objective}
              status={c.status}
              removed={c.removed}
              monthlyBudget={c.dailyBudget ? c.dailyBudget * 30 : 0}
              spend={c.spend}
              impressions={c.impressions}
              clicks={c.clicks}
              conversions={c.conversions}
              cpa={c.cpa}
              launches={c.launches}
              onClick={
                c.marketingCampaignId
                  ? () => navigate(`/campaigns/${c.marketingCampaignId}`)
                  : undefined
              }
            />
          ))
        )}
      </div>
    );
  }

  // ── Active campaigns / recommendations ──────────────────────────────────────
  // One state, three phases:
  //   browse              — ActiveCampaignsView (recs section if any, spinner if generating)
  //   pickMode            — ModeSelectionScreen (only reached when preference is null)
  //   wizard:autopilot/guided — inline wizard
  const savedMode = locationConfigQuery.data?.data?.campaignApprovalPreference ?? null;
  // Provider needs a concrete value even when nothing's saved yet. Autopilot
  // is the safe default for guard semantics — it's the more permissive mode.
  const providerMode = savedMode ?? 'autopilot';

  const startReview = () => {
    if (savedMode === null) {
      setPhase('pickMode');
    } else {
      setPhase(savedMode === 'guided' ? 'wizard:guided' : 'wizard:autopilot');
    }
  };

  if (phase === 'pickMode') {
    return (
      <ManagementModeProvider mode={providerMode} isAgency={!!user?.isAgency}>
        <div className={styles.page}>
          <ModeSelectionScreen
            onChooseAutopilot={() => setPhase('wizard:autopilot')}
            onChooseGuided={() => setPhase('wizard:guided')}
            onCancel={() => setPhase('browse')}
          />
        </div>
      </ManagementModeProvider>
    );
  }

  if (phase === 'wizard:autopilot') {
    return (
      <ManagementModeProvider mode={providerMode} isAgency={!!user?.isAgency}>
        <div className={styles.page}>
          <AutopilotWizard
            recommendedCampaigns={recommendedCampaigns}
            existingCampaigns={existingForWizard}
            onCancel={() => setPhase('browse')}
            onSwitchToGuided={() => setPhase('wizard:guided')}
          />
        </div>
      </ManagementModeProvider>
    );
  }

  if (phase === 'wizard:guided') {
    return (
      <ManagementModeProvider mode={providerMode} isAgency={!!user?.isAgency}>
        <div className={styles.page}>
          <GuidedWizard
            recommendedCampaigns={recommendedCampaigns}
            existingCampaigns={existingForWizard}
            onCancel={() => setPhase('browse')}
            onSwitchToAutopilot={() => setPhase('wizard:autopilot')}
          />
        </div>
      </ManagementModeProvider>
    );
  }

  return (
    <ManagementModeProvider mode={providerMode} isAgency={!!user?.isAgency}>
      <div className={styles.page}>
        <ActiveCampaignsView
          pnCampaigns={liveOrPending}
          externalCampaigns={externalData}
          recommendedCampaigns={recommendedCampaigns}
          isGeneratingRecs={campaignRecsRunning}
          onReviewRecommendations={startReview}
        />
      </div>
    </ManagementModeProvider>
  );
}
